import { useState, useEffect, FormEvent } from 'react';
import { supabase, DJProfile } from '../../../lib/supabase';

interface ContactInfo {
  email: string;
  phone: string;
  location: string;
}

interface ContactFormProps {
  profile?: DJProfile | null;
}

export default function ContactForm({ profile }: ContactFormProps) {
  const sectionTitle = profile?.section_contact_title || 'Conectemos';
  const sectionDescription = profile?.section_contact_description || '¿Tienes un evento próximo? Hablemos sobre cómo puedo hacer que tu celebración sea inolvidable con la mejor música.';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    eventType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: 'mikiesteve@gmail.com',
    phone: '+34 608 22 74 93',
    location: 'Palma de Mallorca'
  });

  useEffect(() => {
    if (profile) {
      setContactInfo({
        email: profile.email || 'mikiesteve@gmail.com',
        phone: profile.phone || '+34 608 22 74 93',
        location: profile.location || 'Palma de Mallorca'
      });
    } else {
      const fetchContact = async () => {
        const { data } = await supabase
          .from('dj_profile')
          .select('email, phone, location')
          .maybeSingle();
        if (data) {
          setContactInfo({
            email: data.email || 'mikiesteve@gmail.com',
            phone: data.phone || '+34 608 22 74 93',
            location: data.location || 'Palma de Mallorca'
          });
        }
      };
      fetchContact();
    }
  }, [profile]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Save message directly to Supabase — reliable, no external service needed
      const { error } = await supabase.from('contact_messages').insert([{
        name: formData.name,
        email: formData.email,
        event_type: formData.eventType || null,
        message: formData.message,
      }]);

      if (error) {
        console.error('Error saving message:', error);
        setSubmitStatus('error');
      } else {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', eventType: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, fuchsia 2px, fuchsia 4px)' }}></div>
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">

        {/* MOBILE layout */}
        <div className="flex flex-col gap-10 lg:hidden">
          {/* 1. Título y descripción */}
          <div>
            <span className="text-cyan-400 text-xs uppercase tracking-[0.3em] font-light">Contacto</span>
            <h2 className="text-4xl font-black text-white mt-4 leading-tight">
              {sectionTitle}
            </h2>
            <p className="text-gray-400 text-base mt-4 leading-relaxed">
              {sectionDescription}
            </p>
          </div>

          {/* 2. Formulario */}
          <div className="bg-black/60 backdrop-blur-sm p-6 rounded-3xl border border-gray-800">
            <form id="contact-form-mobile" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name-m" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Nombre Completo</label>
                <input
                  type="text" id="name-m" name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 text-sm"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label htmlFor="email-m" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Email</label>
                <input
                  type="email" id="email-m" name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 text-sm"
                  placeholder="tu@email.com"
                />
              </div>
              <div>
                <label htmlFor="eventType-m" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Tipo de Evento</label>
                <select
                  id="eventType-m" name="eventType"
                  value={formData.eventType}
                  onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 text-sm cursor-pointer"
                >
                  <option value="">Selecciona un tipo</option>
                  <option value="Boda">Boda</option>
                  <option value="Fiesta Privada">Fiesta Privada</option>
                  <option value="Evento Corporativo">Evento Corporativo</option>
                  <option value="Club/Discoteca">Club/Discoteca</option>
                  <option value="Festival">Festival</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div>
                <label htmlFor="message-m" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Mensaje</label>
                <textarea
                  id="message-m" name="message"
                  value={formData.message}
                  onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }}
                  required rows={4} maxLength={500}
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 resize-none text-sm"
                  placeholder="Cuéntame sobre tu evento..."
                ></textarea>
                <p className="text-gray-500 text-xs mt-2 text-right">{formData.message.length}/500</p>
              </div>
              <button
                type="submit" disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold uppercase text-sm tracking-wider rounded-full hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
              </button>
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500 rounded-xl text-green-400 text-sm text-center">
                  ¡Mensaje enviado con éxito! Te contactaré pronto.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500 rounded-xl text-red-400 text-sm text-center">
                  Hubo un error al enviar el mensaje. Por favor, intenta de nuevo.
                </div>
              )}
            </form>
          </div>

          {/* 3. Email, teléfono y ubicación */}
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-5 bg-black/40 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-lg flex-shrink-0">
                <i className="ri-mail-line text-white text-xl"></i>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
                <a href={`mailto:${contactInfo.email}`} className="text-white hover:text-cyan-400 transition-colors duration-300 cursor-pointer text-sm">
                  {contactInfo.email}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4 p-5 bg-black/40 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-white text-xl"></i>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Teléfono</p>
                <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-white hover:text-cyan-400 transition-colors duration-300 cursor-pointer text-sm">
                  {contactInfo.phone}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4 p-5 bg-black/40 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-lg flex-shrink-0">
                <i className="ri-map-pin-line text-white text-xl"></i>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Ubicación</p>
                <p className="text-white text-sm">{contactInfo.location}</p>
              </div>
            </div>
          </div>
        </div>

        {/* DESKTOP layout — igual que antes */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div>
              <span className="text-cyan-400 text-xs uppercase tracking-[0.3em] font-light">Contacto</span>
              <h2 className="text-5xl lg:text-6xl font-black text-white mt-4 leading-tight">
                {sectionTitle}
              </h2>
              <p className="text-gray-400 text-lg mt-6 leading-relaxed">
                {sectionDescription}
              </p>
            </div>

            <div className="space-y-6 pt-8">
              <div className="flex items-center gap-4 p-5 bg-black/40 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Email</p>
                  <a href={`mailto:${contactInfo.email}`} className="text-white hover:text-cyan-400 transition-colors duration-300 cursor-pointer">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-black/40 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Teléfono</p>
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-white hover:text-cyan-400 transition-colors duration-300 cursor-pointer">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-black/40 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-fuchsia-500 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Ubicación</p>
                  <p className="text-white">{contactInfo.location}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-black/60 backdrop-blur-sm p-8 lg:p-12 rounded-3xl border border-gray-800">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Nombre Completo</label>
                <input
                  type="text" id="name" name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 text-sm"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Email</label>
                <input
                  type="email" id="email" name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 text-sm"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label htmlFor="eventType" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Tipo de Evento</label>
                <select
                  id="eventType" name="eventType"
                  value={formData.eventType}
                  onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                  required
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 text-sm cursor-pointer"
                >
                  <option value="">Selecciona un tipo</option>
                  <option value="Boda">Boda</option>
                  <option value="Fiesta Privada">Fiesta Privada</option>
                  <option value="Evento Corporativo">Evento Corporativo</option>
                  <option value="Club/Discoteca">Club/Discoteca</option>
                  <option value="Festival">Festival</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-400 text-sm mb-2 uppercase tracking-wider">Mensaje</label>
                <textarea
                  id="message" name="message"
                  value={formData.message}
                  onChange={(e) => { if (e.target.value.length <= 500) setFormData({ ...formData, message: e.target.value }); }}
                  required rows={4} maxLength={500}
                  className="w-full px-4 py-3 bg-[#2a2a2a] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all duration-300 resize-none text-sm"
                  placeholder="Cuéntame sobre tu evento..."
                ></textarea>
                <p className="text-gray-500 text-xs mt-2 text-right">{formData.message.length}/500</p>
              </div>

              <button
                type="submit" disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold uppercase text-sm tracking-wider rounded-full hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-500/20 border border-green-500 rounded-xl text-green-400 text-sm text-center">
                  ¡Mensaje enviado con éxito! Te contactaré pronto.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-500/20 border border-red-500 rounded-xl text-red-400 text-sm text-center">
                  Hubo un error al enviar el mensaje. Por favor, intenta de nuevo.
                </div>
              )}
            </form>
          </div>
        </div>

      </div>
    </section>
  );
}
